import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useSettingsStore = defineStore('settings', () => {
  // 状态
  const theme = ref('light') // 'light' | 'dark' | 'system'
  const systemPrefersDark = ref(false)
  const sidebarCollapsed = ref(false)
  const showTagsView = ref(true)
  const showBreadcrumb = ref(true)
  const showHamburger = ref(true)
  const showScreenfull = ref(true)
  const showThemeSwitch = ref(true)
  const showLangSwitch = ref(false)
  const showNotice = ref(true)
  const tagsView = ref([])
  const cachedViews = ref([])

  // 默认主题配置
  const defaultTheme = {
    theme: 'light',
    themeColor: '#409EFF',
    themeStyle: 'default'
  }

  // 主题配置
  const themeConfig = ref({ ...defaultTheme })

  // 计算属性：解析实际主题（system 模式下根据系统偏好决定）
  const resolvedTheme = computed(() => {
    if (theme.value === 'system') {
      return systemPrefersDark.value ? 'dark' : 'light'
    }
    return theme.value
  })

  // 方法
  const setTheme = (newTheme) => {
    theme.value = newTheme
    themeConfig.value.theme = newTheme
    applyTheme()
    saveSettings()
  }

  const setThemeColor = (color) => {
    themeConfig.value.themeColor = color
    saveSettings()
  }

  const setSidebarCollapsed = (collapsed) => {
    sidebarCollapsed.value = collapsed
    saveSettings()
  }

  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
    saveSettings()
  }

  const setShowTagsView = (show) => {
    showTagsView.value = show
    saveSettings()
  }

  const setShowBreadcrumb = (show) => {
    showBreadcrumb.value = show
    saveSettings()
  }

  const setShowHamburger = (show) => {
    showHamburger.value = show
    saveSettings()
  }

  const setShowScreenfull = (show) => {
    showScreenfull.value = show
    saveSettings()
  }

  const setShowThemeSwitch = (show) => {
    showThemeSwitch.value = show
    saveSettings()
  }

  const setShowLangSwitch = (show) => {
    showLangSwitch.value = show
    saveSettings()
  }

  const setShowNotice = (show) => {
    showNotice.value = show
    saveSettings()
  }

  // 标签页相关
  const addTagView = (view) => {
    if (view.name) {
      // 特殊处理：对于账单详情页（BillDetail），只更新已有标签的路径，不创建新标签
      // 这样点击不同账单时会复用同一个标签
      if (view.name === 'BillDetail') {
        const existingTag = tagsView.value.find(v => v.name === 'BillDetail')
        if (existingTag) {
          // 更新已有标签的路径和完整路径
          existingTag.path = view.path
          existingTag.fullPath = view.fullPath || view.path
          existingTag.query = view.query || {}
          // 不需要重新缓存，因为组件实例已被keep-alive复用
          saveTagsView()
          return
        }
      }
      // 通用逻辑：检查是否已存在相同路径的标签
      if (tagsView.value.some(v => v.path === view.path)) return
      const affix = view.meta?.affix || view.path === '/dashboard'
      tagsView.value.push({
        name: view.name,
        path: view.path,
        title: view.meta?.title || view.name,
        fullPath: view.fullPath || view.path,
        meta: { ...view.meta, affix },
        affix
      })
      addCachedView(view)
      saveTagsView()
    }
  }

  const removeTagView = (view) => {
    const index = tagsView.value.findIndex(v => v.path === view.path)
    if (index > -1) {
      tagsView.value.splice(index, 1)
    }
    removeCachedView(view)
    saveTagsView()
  }

  const closeOtherTags = (view) => {
    tagsView.value = tagsView.value.filter(v => v.affix || v.path === view.path)
    cachedViews.value = cachedViews.value.filter(name =>
      tagsView.value.some(v => v.name === name)
    )
    saveTagsView()
  }

  const closeAllTags = () => {
    tagsView.value = tagsView.value.filter(v => v.affix)
    cachedViews.value = cachedViews.value.filter(name =>
      tagsView.value.some(v => v.name === name)
    )
    saveTagsView()
  }

  const clearAllTags = () => {
    tagsView.value = []
    cachedViews.value = []
    sessionStorage.removeItem('tagsView')
  }

  const closeLeftTags = (view) => {
    const index = tagsView.value.findIndex(v => v.path === view.path)
    if (index > 0) {
      const closed = tagsView.value.slice(0, index).filter(v => !v.affix)
      tagsView.value = tagsView.value.filter((v, i) => v.affix || i >= index)
      closed.forEach(v => removeCachedView(v))
      saveTagsView()
    }
  }

  const closeRightTags = (view) => {
    const index = tagsView.value.findIndex(v => v.path === view.path)
    if (index < tagsView.value.length - 1) {
      const closed = tagsView.value.slice(index + 1).filter(v => !v.affix)
      tagsView.value = tagsView.value.filter((v, i) => v.affix || i <= index)
      closed.forEach(v => removeCachedView(v))
      saveTagsView()
    }
  }

  const addCachedView = (view) => {
    if (view.name && !cachedViews.value.includes(view.name)) {
      // 默认缓存所有页面，除非显式设置 keepAlive: false
      if (view.meta?.keepAlive !== false) {
        cachedViews.value.push(view.name)
      }
    }
  }

  const removeCachedView = (view) => {
    const index = cachedViews.value.indexOf(view.name)
    if (index > -1) {
      cachedViews.value.splice(index, 1)
    }
  }

  // 保存设置到本地存储
  const saveSettings = () => {
    localStorage.setItem('settings', JSON.stringify({
      theme: theme.value,
      sidebarCollapsed: sidebarCollapsed.value,
      showTagsView: showTagsView.value,
      showBreadcrumb: showBreadcrumb.value,
      showHamburger: showHamburger.value,
      showScreenfull: showScreenfull.value,
      showThemeSwitch: showThemeSwitch.value,
      showLangSwitch: showLangSwitch.value,
      showNotice: showNotice.value,
      themeConfig: themeConfig.value
    }))
  }

  // 保存标签页到 sessionStorage（仅在当前会话有效）
  const saveTagsView = () => {
    try {
      const data = tagsView.value.map(v => ({
        name: v.name,
        path: v.path,
        title: v.title,
        fullPath: v.fullPath || v.path,
        affix: v.affix,
        meta: v.meta
      }))
      sessionStorage.setItem('tagsView', JSON.stringify(data))
    } catch (e) {
      // ignore
    }
  }

  // 恢复标签页
  const restoreTagsView = () => {
    try {
      const saved = sessionStorage.getItem('tagsView')
      if (saved) {
        const data = JSON.parse(saved)
        if (Array.isArray(data) && data.length > 0) {
          tagsView.value = data
          // 恢复缓存视图
          data.forEach(v => {
            if (v.name && !cachedViews.value.includes(v.name) && v.meta?.keepAlive !== false) {
              cachedViews.value.push(v.name)
            }
          })
          return true
        }
      }
    } catch (e) {
      // ignore
    }
    return false
  }

  // 从本地存储加载设置
  const loadSettings = () => {
    try {
      const savedSettings = localStorage.getItem('settings')
      if (savedSettings) {
        const settings = JSON.parse(savedSettings)
        theme.value = settings.theme || 'light'
        sidebarCollapsed.value = settings.sidebarCollapsed || false
        showTagsView.value = settings.showTagsView !== false
        showBreadcrumb.value = settings.showBreadcrumb !== false
        showHamburger.value = settings.showHamburger !== false
        showScreenfull.value = settings.showScreenfull !== false
        showThemeSwitch.value = settings.showThemeSwitch !== false
        showLangSwitch.value = settings.showLangSwitch || false
        showNotice.value = settings.showNotice !== false
        themeConfig.value = { ...defaultTheme, ...settings.themeConfig }
      }
    } catch (error) {
      console.error('加载设置失败:', error)
    }
  }

  // 重置设置
  const resetSettings = () => {
    theme.value = 'light'
    sidebarCollapsed.value = false
    showTagsView.value = true
    showBreadcrumb.value = true
    showHamburger.value = true
    showScreenfull.value = true
    showThemeSwitch.value = true
    showLangSwitch.value = false
    showNotice.value = true
    themeConfig.value = { ...defaultTheme }
    tagsView.value = []
    cachedViews.value = []
    localStorage.removeItem('settings')
    sessionStorage.removeItem('tagsView')
  }

  // 应用主题到DOM
  const applyTheme = () => {
    const root = document.documentElement
    const isDark = resolvedTheme.value === 'dark'

    // 添加过渡动画类
    root.classList.add('theme-transition')

    if (isDark) {
      root.classList.add('dark')
      root.setAttribute('data-vxe-ui-theme', 'dark')
    } else {
      root.classList.remove('dark')
      root.removeAttribute('data-vxe-ui-theme')
    }

    // 应用主题色
    root.style.setProperty('--el-color-primary', themeConfig.value.themeColor)

    // 过渡结束后移除过渡类
    setTimeout(() => {
      root.classList.remove('theme-transition')
    }, 350)
  }

  // 初始化系统主题偏好监听
  const initSystemThemeListener = () => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = mql.matches
    mql.addEventListener('change', (e) => {
      systemPrefersDark.value = e.matches
      if (theme.value === 'system') {
        applyTheme()
      }
    })
  }

  // 初始化
  const init = () => {
    loadSettings()
    restoreTagsView()
    initSystemThemeListener()
    applyTheme()
  }

  return {
    // 状态
    theme,
    resolvedTheme,
    sidebarCollapsed,
    showTagsView,
    showBreadcrumb,
    showHamburger,
    showScreenfull,
    showThemeSwitch,
    showLangSwitch,
    showNotice,
    tagsView,
    cachedViews,
    themeConfig,

    // 方法
    setTheme,
    setThemeColor,
    setSidebarCollapsed,
    toggleSidebar,
    setShowTagsView,
    setShowBreadcrumb,
    setShowHamburger,
    setShowScreenfull,
    setShowThemeSwitch,
    setShowLangSwitch,
    setShowNotice,
    addTagView,
    removeTagView,
    closeOtherTags,
    closeAllTags,
    clearAllTags,
    closeLeftTags,
    closeRightTags,
    addCachedView,
    removeCachedView,
    saveSettings,
    loadSettings,
    resetSettings,
    saveTagsView,
    restoreTagsView,
    applyTheme,
    init
  }
})